import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { loadTBOutbreakAlerts, TB_COUNTRIES } from "../../services/tbOutbreakApi";
import "./TBOutbreakAlert.css";

const SIGNAL_SEVERITIES = new Set(["warning", "serious", "critical"]);
const DISMISSED_RUN_STORAGE_KEY = "escort.tbOutbreakAlert.dismissedRun";

function toDisplayName(value, fallback) {
  if (!value) {
    return fallback;
  }

  return String(value)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function TBOutbreakAlert() {
  const navigate = useNavigate();

  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("SE");
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [alertRunId, setAlertRunId] = useState("");

  useEffect(() => {
    let active = true;

    loadTBOutbreakAlerts()
      .then((entries) => {
        if (!active) {
          return;
        }

        const activeAlerts = entries.filter(({ metrics: item }) =>
          SIGNAL_SEVERITIES.has(String(item?.summary?.severity || "").toLowerCase()) ||
          Boolean(item?.summary?.first_breach_month)
        );
        setAlerts(activeAlerts);
        if (!activeAlerts.length) return;
        const data = activeAlerts[0].metrics;
        setSelectedCountry(activeAlerts[0].countryIso);
        setMetrics(data);

        const severity = String(data?.summary?.severity || "").toLowerCase();
        const hasDetectedSignal =
          SIGNAL_SEVERITIES.has(severity) ||
          Boolean(data?.summary?.first_breach_month);

        const runId =
          activeAlerts.map(({ countryIso, metrics: item }) =>
            `${countryIso}:${item?.meta?.generated_at || "unknown"}`
          ).join("|");

        setAlertRunId(runId);

        if (hasDetectedSignal) {
          let wasDismissedInThisSession = false;

          try {
            wasDismissedInThisSession =
              sessionStorage.getItem(DISMISSED_RUN_STORAGE_KEY) === runId;
          } catch (storageError) {
            console.warn(
              "TB outbreak alert session state could not be read:",
              storageError
            );
          }

          setIsOpen(!wasDismissedInThisSession);
          setIsMinimized(wasDismissedInThisSession);
        }
      })
      .catch((error) => {
        console.warn("TB outbreak alert data could not be loaded:", error);
      });

    return () => {
      active = false;
    };
  }, []);

  const countryName = useMemo(
    () => toDisplayName(metrics?.meta?.country, "Sweden"),
    [metrics?.meta?.country]
  );

  const localScopeName = metrics?.local?.scope?.name || TB_COUNTRIES[selectedCountry].localLabel;
  const summary = metrics?.summary;
  const concentration = metrics?.local?.contrast_with_national;

  const minimizeAlert = () => {
    setIsOpen(false);
    setIsMinimized(true);

    if (alertRunId) {
      try {
        sessionStorage.setItem(DISMISSED_RUN_STORAGE_KEY, alertRunId);
      } catch (storageError) {
        console.warn(
          "TB outbreak alert session state could not be saved:",
          storageError
        );
      }
    }
  };

  const reopenAlert = () => {
    setIsMinimized(false);
    setIsOpen(true);
  };

  if (!summary) {
    return null;
  }

  return (
    <>
      {isMinimized && (
        <button
          type="button"
          className="tb-alert-minimized"
          onClick={reopenAlert}
          aria-label="Open TB health threat alert"
          title="Open health threat alert"
        >
          <span className="tb-alert-minimized-icon" aria-hidden="true">
            !
          </span>
          <span className="tb-alert-minimized-badge" aria-hidden="true">
            {alerts.length}
          </span>
        </button>
      )}

      {isOpen && (
        <div className="tb-alert-overlay">
          <section
            className="tb-alert-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tb-alert-title"
            aria-describedby="tb-alert-description"
          >
            <button
              type="button"
              className="tb-alert-close"
              aria-label="Minimize TB outbreak alert"
              onClick={minimizeAlert}
            >
              ×
            </button>

            <div className="tb-alert-topline">
              <div className="tb-alert-warning-icon" aria-hidden="true">
                <svg
                  viewBox="0 0 100 88"
                  role="presentation"
                  focusable="false"
                >
                  <defs>
                    <linearGradient
                      id="tb-alert-warning-gradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#f36a43" />
                      <stop offset="100%" stopColor="#e65032" />
                    </linearGradient>
                  </defs>

                  <path
                    d="M46.5 10.5
                       Q50 4.5 53.5 10.5
                       L92 74.5
                       Q97 83 87.5 83
                       H12.5
                       Q3 83 8 74.5
                       Z"
                    fill="url(#tb-alert-warning-gradient)"
                  />
                  <text
                    x="50"
                    y="52"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    !
                  </text>
                </svg>
              </div>

              <h2 id="tb-alert-title">
                {alerts.length} Potential Health {alerts.length === 1 ? "Threat" : "Threats"} Detected
              </h2>
            </div>

            <p
              id="tb-alert-description"
              className="tb-alert-outbreak-title"
            >
              Potential Tuberculosis Outbreak in{" "}
              <strong>{countryName}</strong>
            </p>

            {alerts.length > 1 && (
              <label>
                Country{" "}
                <select
                  value={selectedCountry}
                  onChange={(event) => {
                    const entry = alerts.find((item) => item.countryIso === event.target.value);
                    if (entry) {
                      setSelectedCountry(entry.countryIso);
                      setMetrics(entry.metrics);
                    }
                  }}
                >
                  {alerts.map(({ countryIso }) => (
                    <option key={countryIso} value={countryIso}>{TB_COUNTRIES[countryIso].label}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="tb-alert-metrics">
              <div className="tb-alert-metric">
                <span>Latest alerts</span>
                <strong>{summary.latest_alerts ?? "—"}</strong>
              </div>

              <div className="tb-alert-metric">
                <span>Expected</span>
                <strong>{summary.latest_expected ?? "—"}</strong>
              </div>

              <div className="tb-alert-metric">
                <span>Excess</span>
                <strong>
                  {summary.latest_ratio !== undefined
                    ? `${summary.latest_ratio}×`
                    : "—"}
                </strong>
              </div>

              <div className="tb-alert-metric">
                <span>{localScopeName} concentration</span>
                <strong>
                  {concentration?.concentration_ratio !== undefined
                    ? `${concentration.concentration_ratio}×`
                    : "—"}
                </strong>
              </div>
            </div>

            <div className="tb-alert-footer">
              <div className="tb-alert-actions">
                <button
                  type="button"
                  className="tb-alert-dismiss"
                  onClick={minimizeAlert}
                >
                  Dismiss
                </button>

                <button
                  type="button"
                  className="tb-alert-view"
                  onClick={() => navigate(`/tb-outbreak?country=${selectedCountry}`)}
                >
                  View details
                </button>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
